import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import heroVideo from "@/assets/hero-worship.mp4.asset.json";
import footprintStamp from "@/assets/footprint-stamp.png";

export function Hero() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const handleStartJourney = () => {
    if (loading) return;
    navigate(user ? "/dashboard" : "/auth");
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Video background */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          poster=""
        >
          <source src={heroVideo.url} type="video/mp4" />
        </video>
        {/* Heavy cinematic overlay — near black */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
      </div>

      <div className="container relative z-10 px-6 py-32 md:py-40 lg:py-48">
        <div className="max-w-6xl">
          {/* Oversized iconic headline */}
          <motion.h1
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="leading-[0.85] tracking-[-0.04em] mb-8"
          >
            <span
              className="block text-[4rem] sm:text-[5.5rem] md:text-[7rem] lg:text-[9rem] xl:text-[11rem] font-black text-white uppercase"
              style={{
                fontFamily: "'Playfair Display', serif",
                textShadow: "0 4px 80px rgba(0,0,0,0.6)",
              }}
            >
              Healed
            </span>
            <span
              className="block text-[4rem] sm:text-[5.5rem] md:text-[7rem] lg:text-[9rem] xl:text-[11rem] font-black text-white uppercase"
              style={{
                fontFamily: "'Playfair Display', serif",
                textShadow: "0 4px 80px rgba(0,0,0,0.6)",
              }}
            >
              People{" "}
              <motion.span
                className="italic"
                style={{ color: "hsl(var(--accent))" }}
                animate={{ opacity: [0.85, 1, 0.85] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                Heal
              </motion.span>
            </span>
            <span
              className="block text-[4rem] sm:text-[5.5rem] md:text-[7rem] lg:text-[9rem] xl:text-[11rem] font-black text-white uppercase"
              style={{
                fontFamily: "'Playfair Display', serif",
                textShadow: "0 4px 80px rgba(0,0,0,0.6)",
              }}
            >
              People
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-lg md:text-xl text-white/60 mb-12 leading-relaxed max-w-2xl font-sans"
          >
            Spiritual and narcissistic abuse thrive where biblical illiteracy,
            unchecked authority, and silence are normalized. We're changing that.
          </motion.p>

          {/* Stamp lockup — bigger, bolder, red border */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 1, type: "spring", damping: 12 }}
            className="flex items-center gap-6 mb-14"
          >
            <div className="relative">
              <div className="absolute -inset-2 rounded-full border-[3px] border-red-500/80 animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
              <motion.img
                src={footprintStamp}
                alt="Stamping out spiritual abuse"
                className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 relative z-10 drop-shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                animate={{ rotate: [0, -8, 0, 8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
            <div>
              <p
                className="text-lg md:text-xl font-black text-red-400 uppercase tracking-[0.3em]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Stamp Out
              </p>
              <p
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase tracking-[0.1em]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Spiritual Abuse
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-4 mb-8"
          >
            <Button
              onClick={handleStartJourney}
              disabled={loading}
              className="h-16 sm:h-18 px-10 sm:px-14 text-lg sm:text-xl font-black bg-white text-black hover:bg-white/90 rounded-full shadow-[0_8px_40px_-6px_rgba(255,255,255,0.3)] hover:shadow-[0_16px_60px_-6px_rgba(255,255,255,0.45)] transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] uppercase tracking-wider"
            >
              Start Your Healing Journey
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="text-sm text-white/30 italic font-serif"
          >
            Already have an account?{" "}
            <button
              onClick={() => navigate("/auth")}
              className="underline underline-offset-4 hover:text-white/50 transition-colors"
            >
              Sign in
            </button>{" "}
            to continue your journey.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
